import { ModelContextProps } from "contexts-types";
import { ModelDossierController } from "controllers/ModelDossierController";
import { IModelDossierInputModel, IOrderFieldInputModel } from "inputModels";
import { Columns, FilterProps, Props } from "props";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { IModelDossierViewModel } from "viewModels";
import { useAuth } from "./auth";
import { useExternalFont } from "./externalFontContext";
import { SearchType } from "types/enums";
import { useAlert } from "./alert";

const PAGE_SIZE = 15;
const ModelContext = createContext<ModelContextProps>({} as ModelContextProps);

const Model: React.FC<Props> = ({ children }) => {
  const { user, logged } = useAuth();
  const { externalFonts } = useExternalFont();
  const alert = useAlert();
  const [models, setModels] = useState<IModelDossierViewModel[]>([]);
  const [orderBy, setOrder] = useState<IOrderFieldInputModel<Columns>[]>([]);
  const [filter, setFilter] = useState<FilterProps>();
  const {
    data: results,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery(
    ["modelsDossier", orderBy, filter],
    ({ pageParam = 1 }) =>
      ModelDossierController.getByModelId({
        orderBy,
        page: pageParam,
        take: 15,
        term: filter?.term,
        filter: filter?.term ? filter?.filter : undefined,
      }).then((res) => res.data),
    {
      enabled: !!user?.id && logged,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
    }
  );

  const handleFetch = () => {
    refetch();
  };

  const handleCreateModel = async (
    modelName: string,
    selectedFonts: string[]
  ): Promise<boolean> => {
    const input: IModelDossierInputModel = {
      name: modelName,
      fonts: selectedFonts,
      createdById: user.id,
      type: selectedFonts.some(
        (key) =>
          externalFonts.find((f) => f.key === key)?.type === SearchType.CPF
      )
        ? SearchType.CPF
        : selectedFonts.some(
            (key) =>
              externalFonts.find((f) => f.key === key)?.type === SearchType.CNPJ
          )
        ? SearchType.CNPJ
        : SearchType.BOTH,
    };

    await ModelDossierController.insert(input)
      .then(() => {
        handleFetch(); //[TODO] Ajustar o metodo para ja retornar o model criado
      })
      .catch(() => {
        alert.show({
          type: "error",
          title: "Erro",
          description: "Erro ao criar o modelo. Tente novamente.",
        });
      });

    return true;
  };

  useEffect(() => {
    setModels(results?.pages?.flat() ?? []);
  }, [results, setModels]);

  const handleFetchNextPage = () => {
    fetchNextPage();
  };

  return (
    <ModelContext.Provider
      value={{
        orderBy,
        filter,
        setFilter,
        models,
        setModels,
        setOrder,
        handleFetchNextPage,
        isFetchingNextPage,
        isLoading,
        hasNextPage,
        handleFetch,
        handleCreateModel,
      }}
    >
      {children}
    </ModelContext.Provider>
  );
};

export const useModel: () => ModelContextProps = () => useContext(ModelContext);

export default Model;
